import React, { useContext } from "react";
import axios from "axios";
import {
    TextField,
    Dropdown,
    IDropdownOption,
    PrimaryButton,
    Stack,
    MessageBar,
    MessageBarType,
    Spinner
} from "@fluentui/react";
import { IEntity, IUser } from "../types";
import { LookupService } from "../services";
import { DataContext } from "../components/DataContext";
import { CrUserPicker } from "../components/cr/CrUserPicker";
import { CrLoadingOverlay } from "../components/cr/CrLoadingOverlay";

interface IOrganisationDetailsState {
    organisationName: string;
    address: string;
    country?: number;
    sector?: number;
    numberOfStaff: string;
    website: string;
    orgPhone: string;

    title?: string | number;
    userId?: number;
    email: string;
    phone: string;
}

export const OrganisationDetails: React.FC = () => {

    const [loading, setLoading] = React.useState<boolean>(true);
    const [saving, setSaving] = React.useState<boolean>(false);

    const { dataServices } = useContext(DataContext);

    const [customerCountries, setCustomerCountries] = React.useState<IEntity[]>([]);
    const [customerSectors, setCustomerSectors] = React.useState<IEntity[]>([]);

    const [titleOptions] = React.useState<IDropdownOption[]>([
        { key: "Mr", text: "Mr" },
        { key: "Mrs", text: "Mrs" },
        { key: "Miss", text: "Miss" },
        { key: "Ms", text: "Ms" },
        { key: "Dr", text: "Dr" },
        { key: "Prof", text: "Prof" }
    ]);

    const [details, setDetails] = React.useState<IOrganisationDetailsState>({
        organisationName: "",
        address: "",
        country: undefined,
        sector: undefined,
        numberOfStaff: "",
        website: "",
        orgPhone: "",

        title: undefined,
        userId: undefined,
        email: "",
        phone: ""
    });

    const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [superUsers, setSuperUsers] = React.useState<IUser[]>([]);

    React.useEffect(() => {
        loadData();
    }, []);



    const loadData = async () => {

        try {

            setLoading(true);

            const coreApi = process.env.REACT_APP_ZedX_Net_API!;

            // countries
            const countriesUrl = `${coreApi}/CustomerCountries/GetAll`;

            // sectors
            const sectorsUrl = `${coreApi}/CustomerSectors/GetAll`;

            const countriesResponse = await axios.get(countriesUrl);
            const sectorsResponse = await axios.get(sectorsUrl);

            setCustomerCountries(countriesResponse.data);
            setCustomerSectors(sectorsResponse.data);

            const susers = await dataServices.userService.readAllSuperUsers();
            setSuperUsers(susers);

            const organisationDetails = await dataServices.accountService.getOrganisationDetails();
            console.log('organisationDetails', organisationDetails);

            const selectedUser = susers.find(u => u.ID === organisationDetails.primaryUserID);

            setDetails({
                organisationName: organisationDetails.organisationName || "",
                address: organisationDetails.address || "",
                country: organisationDetails.countryID,
                sector: organisationDetails.sectorID,
                numberOfStaff: organisationDetails.numberOfStaff || "",
                website: organisationDetails.website || "",
                orgPhone: organisationDetails.orgPhone || "",

                title: selectedUser?.UserTitle || "",
                userId: organisationDetails.primaryUserID,
                email: selectedUser?.EmailAddress || "",
                phone: selectedUser?.Phone || ""
            });

        }
        catch (err) {

            console.error(err);
            setErrorMessage("Failed to load organisation details.");
        }
        finally {

            setLoading(false);
        }
    };

    const update = (field: keyof IOrganisationDetailsState, value: any) => {

        setDetails(prev => ({
            ...prev,
            [field]: value
        }));

        if (validationErrors[field]) {

            setValidationErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const validateForm = (): boolean => {

        const errors: Record<string, string> = {};

        if (!details.organisationName?.trim()) {
            errors.organisationName = "Organisation Name is required";
        }

        if (!details.address?.trim()) {
            errors.address = "Primary Address is required";
        }

        if (!details.country) {
            errors.country = "Country is required";
        }

        if (!details.sector) {
            errors.sector = "Sector is required";
        }

        if (!details.title) {
            errors.title = "Title is required";
        }

        if (!details.userId) {
            errors.userId = "User is required";
        }

        if (!details.email?.trim()) {
            errors.email = "Email is required";
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) {
            errors.email = "Please enter a valid email address";
        }

        setValidationErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {

        try {

            setSuccessMessage("");
            setErrorMessage("");

            if (!validateForm()) {
                return;
            }

            setSaving(true);

            const payload = {
                organisationName: details.organisationName,
                address: details.address,
                countryID: details.country,
                sectorID: details.sector,
                numberOfStaff: details.numberOfStaff,
                website: details.website,
                orgPhone: details.orgPhone,
                primaryUserID: details.userId
            };

            await dataServices.accountService.saveOrganisationDetails(payload);

            setSuccessMessage("Organisation details updated successfully.");
        }
        catch (err) {

            console.error(err);
            setErrorMessage("Failed to save organisation details.");
        }
        finally {
            setSaving(false);
        }
    };

    const changeUserPicker = (value: any[]) => {

        const userId = value.length === 1 ? value[0] : null;

        const selectedUser = superUsers.find(u => u.ID === userId);

        setDetails(prev => ({
            ...prev,
            userId: userId ?? undefined,
            title: selectedUser?.UserTitle || "",
            email: selectedUser?.EmailAddress || "",
            phone: selectedUser?.Phone || ""
        }));
    };

    // if (loading) {

    //     return (
    //         <div className="orgdetails-container">
    //             <Spinner label="Loading organisation details..." />
    //         </div>
    //     );
    // }

    return (
        <>
            <CrLoadingOverlay isLoading={loading} opaque={true} />
            <div className="orgdetails-container">

                <h2 className="orgdetails-heading">
                    Organisation Details
                </h2>

                {successMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={false}
                    >
                        {successMessage}
                    </MessageBar>
                )}

                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                {/* ORGANISATION SECTION */}
                <div className="orgdetails-section">

                    <div className="orgdetails-subtitle">
                        Organisation Information
                    </div>

                    <div className="orgdetails-grid">

                        <div className="orgdetails-field">
                            <TextField
                                label="Organisation Name"
                                value={details.organisationName}
                                onChange={(_, v) => update("organisationName", v || "")}
                                errorMessage={validationErrors.organisationName}
                            />
                        </div>

                        <div className="orgdetails-field">
                            <TextField
                                label="Primary Address"
                                value={details.address}
                                onChange={(_, v) => update("address", v || "")}
                                errorMessage={validationErrors.address}
                            />
                        </div>

                        <div className="orgdetails-field">
                            <Dropdown
                                label="Country"
                                options={LookupService.entitiesToSelectableOptions(customerCountries)}
                                selectedKey={details.country}
                                onChange={(_, o) => update("country", o?.key)}
                                errorMessage={validationErrors.country}
                                disabled
                            />
                        </div>

                        <div className="orgdetails-field">
                            <Dropdown
                                label="Sector"
                                options={LookupService.entitiesToSelectableOptions(customerSectors)}
                                selectedKey={details.sector}
                                onChange={(_, o) => update("sector", o?.key)}
                                errorMessage={validationErrors.sector}
                                disabled
                            />
                        </div>

                        <div className="orgdetails-field">
                            <TextField
                                label="Number of Staff"
                                value={details.numberOfStaff}
                                onChange={(_, v) => update("numberOfStaff", v || "")}
                            />
                        </div>

                        <div className="orgdetails-field">
                            <TextField
                                label="Website"
                                value={details.website}
                                onChange={(_, v) => update("website", v || "")}
                            />
                        </div>

                        <div className="orgdetails-field">
                            <TextField
                                label="Organisation Phone"
                                value={details.orgPhone}
                                onChange={(_, v) => update("orgPhone", v || "")}
                            />
                        </div>

                    </div>

                </div>

                {/* USER SECTION */}
                <div className="orgdetails-section">

                    <div className="orgdetails-subtitle">
                        Primary Contact
                    </div>

                    <div className="orgdetails-grid">

                        {/* ROW 1 */}

                        <div className="orgdetails-field">
                            <Dropdown
                                label="Title"
                                options={titleOptions}
                                selectedKey={details.title}
                                disabled
                            />
                        </div>

                        <div className="orgdetails-field">

                            <CrUserPicker
                                label="User"
                                required={true}
                                users={superUsers}
                                selectedUsers={details.userId ? [details.userId] : []}
                                onChange={changeUserPicker}
                                errorMessage={validationErrors.userId}
                            />

                        </div>

                        {/* ROW 2 */}

                        <div className="orgdetails-field">
                            <TextField
                                label="Email"
                                value={details.email}
                                disabled
                                readOnly
                            />
                        </div>

                        <div className="orgdetails-field">
                            <TextField
                                label="Phone"
                                value={details.phone}
                                disabled
                                readOnly
                            />
                        </div>

                    </div>

                </div>

                {/* ACTIONS */}
                <Stack
                    horizontal
                    tokens={{ childrenGap: 12 }}
                    className="orgdetails-actions"
                >

                    <PrimaryButton
                        text={saving ? "Saving..." : "Save Changes"}
                        onClick={handleSave}
                        disabled={saving}
                    />

                </Stack>

            </div>
        </>
    );
};